import type { DepositSummary } from './deposit-badge';

/**
 * Would this STAFF-marked status change forfeit the customer's deposit?
 *
 * This is the client-side mirror of the server's §4.4 rules in
 * `packages/features/src/appointments/services/update-appointment/update-appointment.service.ts`.
 * The server is the authority and forfeits on its own regardless of what this
 * returns — this exists ONLY to decide whether to WARN a staff member before
 * they take money off a customer. Getting it wrong is therefore not a
 * correctness bug in the forfeit, but it IS a "we confiscated a deposit with
 * no confirmation" bug, which is worse for the clinic.
 *
 * The rules, kept deliberately in the same order the service applies them:
 *
 *   - NO-SHOW forfeits whenever `depositForfeitOnNoShow` is on. It is judged
 *     on attendance, not timing — the appointment is in the past by
 *     definition, so the deadline never enters into it. It is NOT gated by
 *     `depositForfeitOnLateCancel`; wiring the two to one flag would silently
 *     stop no-show forfeiting for every org that turned late-cancel off.
 *   - CANCELLED forfeits only when `depositForfeitOnLateCancel` is on AND the
 *     booking is LATE, i.e. strictly inside the deadline.
 *   - A deadline of 0 means the org set no window at all, so nothing is ever
 *     late and a cancel never forfeits.
 *   - Only a PAID deposit is forfeitable. A pending request was never
 *     captured; there is nothing to keep.
 *
 * The `?? true` / `?? 24` fallbacks mirror the columns' NOT NULL defaults, so
 * an org row that has not loaded yet warns rather than silently staying quiet.
 */
export interface ForfeitPolicy {
  cancellationRescheduleDeadlineHours?: number | null;
  depositForfeitOnLateCancel?: boolean | null;
  depositForfeitOnNoShow?: boolean | null;
}

/** The staff-marked outcomes that can cost a customer their deposit. */
export type ForfeitAction = 'cancelled' | 'no_show';

const HOUR_MS = 60 * 60 * 1000;

export function wouldForfeitDeposit(input: {
  action: ForfeitAction;
  /** The booking's deposit, if it has one. */
  deposit: DepositSummary | null | undefined;
  /** Appointment start as an ISO string (real UTC instant). */
  startDate: string;
  policy: ForfeitPolicy | null | undefined;
  /** Injectable for tests; defaults to now. */
  now?: Date;
}): boolean {
  // Only a captured deposit can be kept.
  if (input.deposit?.status !== 'paid') return false;

  if (input.action === 'no_show') {
    return input.policy?.depositForfeitOnNoShow ?? true;
  }

  if (!(input.policy?.depositForfeitOnLateCancel ?? true)) return false;

  const deadlineHours = input.policy?.cancellationRescheduleDeadlineHours ?? 24;
  if (deadlineHours <= 0) return false;

  // An unknown start date cannot RULE OUT lateness, so warn rather than stay
  // silent — same fail-SAFE direction as the `?? true` / `?? 24` fallbacks
  // above. Staying quiet here would be fail-OPEN: the server would forfeit a
  // deposit that the staff member was never asked about, which is the exact
  // failure this dialog exists to prevent.
  const start = new Date(input.startDate).getTime();
  if (Number.isNaN(start)) return true;

  const now = (input.now ?? new Date()).getTime();
  const hoursUntilStart = (start - now) / HOUR_MS;

  // Strictly inside the deadline is late; exactly at it is not. Mirrors
  // `evaluateBookingPolicy`'s `<=` free-window boundary.
  return hoursUntilStart < deadlineHours;
}
